import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { usePreferences, type ThemePreference } from "./storage";

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: "dark" | "light";
  setPreference: (p: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
const QUERY = "(prefers-color-scheme: dark)";

function systemTheme(): "dark" | "light" {
  return window.matchMedia(QUERY).matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [prefs, update] = usePreferences();
  const [system, setSystem] = useState<"dark" | "light">(() => systemTheme());

  useEffect(() => {
    const mql = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const resolved = prefs.theme === "system" ? system : prefs.theme;

  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
    document.documentElement.dataset.density = prefs.density;
  }, [resolved, prefs.density]);

  useEffect(() => {
    const before = () => {
      document.documentElement.dataset.theme = "light";
    };
    const after = () => {
      document.documentElement.dataset.theme = resolved;
    };
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, [resolved]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference: prefs.theme, resolved, setPreference: (theme) => update({ theme }) }),
    [prefs.theme, resolved, update],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
